import PropTypes from "prop-types";
import { shallowEqual, useSelector } from "react-redux";
import LottieComponent from "../LottieComponent";
import { LOADER } from "../../../constants/pageConstants/pdpConstants";

/**
 * LottieLoader component that displays a Lottie animation with a white circular background and drop shadow.
 * @component
 * @param {string} position - CSS position value (default: "fixed")
 * @param {string} zIndex - Tailwind z-index class (default: "z-10")
 * @param {boolean} isBackDrop - Whether to show the backdrop blur overlay (default: true)
 * @param {string} backdropBlur - Backdrop blur amount, e.g. "1.5px" or "5px" (default: "1.5px")
 * @param {boolean} isShowLottie - Whether to show the Lottie animation (default: true)
 * @returns {JSX.Element} The LottieLoader component.
 */
const LottieLoader = ({
  position = "fixed",
  zIndex = "z-10",
  isBackDrop = true,
  backdropBlur = "1.5px",
  isShowLottie = true,
}) => {
  const { lottieIcon } = useSelector(
    (state) => ({
      lottieIcon: state?.lottieIcons?.icons,
    }),
    shallowEqual,
  );

  const lottieIconUrl = lottieIcon[LOADER.toLowerCase()]?.iconUrl;

  if (!lottieIconUrl && isShowLottie) {
    return null;
  }
  return (
    <div className={`${position} left-0 top-0 ${zIndex} flex h-full w-full items-center justify-center`}>
      {/* Backdrop with blur */}
      {isBackDrop && (
        <div
          className={`${position} left-0 top-0 h-full w-full`}
          style={{
            background: "rgba(255, 255, 255, 0.16)",
            backdropFilter: `blur(${backdropBlur})`,
          }}
        />
      )}
      {/* Loader content */}
      {isShowLottie && (
        <div className="relative flex h-[96px] w-[96px] items-center justify-center rounded-full md:h-[140px] md:w-[140px]">
          {/* Background with blur */}
          <div className="absolute inset-0 rounded-full bg-white-900 blur-[10px] md:blur-[25px]" />
          {/* Lottie component - not affected by blur */}
          <span className="relative z-10 h-[64px] w-[64px] md:h-[80px] md:w-[80px]">
            <LottieComponent animationPath={lottieIconUrl} />
          </span>
        </div>
      )}
    </div>
  );
};

LottieLoader.propTypes = {
  position: PropTypes.string,
  zIndex: PropTypes.string,
  isBackDrop: PropTypes.bool,
  backdropBlur: PropTypes.string,
  isShowLottie: PropTypes.bool,
};

export default LottieLoader;
